import { ILocalizedTestElement } from "@/modules/assessments/types/test.interface";
import { ITestAnswerTimes } from "@/shared/components/charts/types/chartTypes";
import { ITestChoice, QuestionTypes } from "@/shared/components/candidate/scenes/TestElement/TestElement";
import { minToSec } from "@/shared/utils/common.utils";

/**
 * Returns a Set of element IDs that belong to timed-out sections.
 * Iterates elements in order to properly track section boundaries,
 * so that even elements without `startsSection` are associated with their section.
 * @param questionsForTest - Flat list of elements for a single test only (e.g. from getQuestionsForTest).
 */
export function getTimedOutSectionElementIds(
  questionsForTest: ILocalizedTestElement[],
  sectionsTime: ITestAnswerTimes,
): Set<number> {
  const timedOutElementIds = new Set<number>();
  let currentSectionKey: string | null = null;
  let isCurrentSectionTimedOut = false;

  for (const element of questionsForTest) {
    if (element.config.startsSection) {
      currentSectionKey = element.config.key;
      const timeLimit = element.config.startsSection.timeLimit;

      if (currentSectionKey && timeLimit) {
        const sectionTime = sectionsTime[currentSectionKey];
        isCurrentSectionTimedOut = sectionTime ? sectionTime.time >= minToSec(timeLimit) : false;
      } else {
        isCurrentSectionTimedOut = false;
      }
    }

    if (isCurrentSectionTimedOut) {
      timedOutElementIds.add(element.elementId);
    }
  }

  return timedOutElementIds;
}

/**
 * Generates random answers for a MostLeast pair, skipping elements already answered.
 * Returns an empty array if both are already answered.
 * Ensures the two chosen statements differ.
 */
export function processMostLeastPair(
  first: ILocalizedTestElement,
  second: ILocalizedTestElement,
  existingAnswers: Map<number, ITestChoice> = new Map(),
): ITestChoice[] {
  const firstAnswer = existingAnswers.get(first.elementId);
  const secondAnswer = existingAnswers.get(second.elementId);

  if (firstAnswer && secondAnswer) return [];

  const getChoiceValue = (src: ILocalizedTestElement, answer: ITestChoice) => {
    const choice = src.config.payload.choices.find((c) => c.key === answer.choiceKey)?.choice;
    return choice ? String(choice) : undefined;
  };

  const resolvedFirst =
    firstAnswer ?? pickRandomChoice(first, secondAnswer ? getChoiceValue(second, secondAnswer) : undefined);

  const result: ITestChoice[] = [];
  if (!firstAnswer) result.push(resolvedFirst);
  if (!secondAnswer) result.push(pickRandomChoice(second, getChoiceValue(first, resolvedFirst)));
  return result;
}

/**
 * Picks a single random choice for the given element, optionally excluding a statement value
 * already chosen by the pair partner.
 */
function pickRandomChoice(element: ILocalizedTestElement, choiceValueToExclude?: string): ITestChoice {
  const choices = element.config.payload.choices;
  const pool =
    choiceValueToExclude !== undefined ? choices.filter((c) => String(c.choice) !== choiceValueToExclude) : choices;
  if (pool.length === 0) {
    throw new Error(`Element ${element.elementId} has no choices remaining after excluding "${choiceValueToExclude}"`);
  }
  const idx = Math.floor(Math.random() * pool.length);
  return { elementId: element.elementId, choiceKey: pool[idx].key };
}

/**
 * Collects all answerable (non-Text) elements for a given test across all pages,
 * excluding any that belong to timed-out sections.
 * @param questionsForTest - Flat list of elements for a single test only (e.g. from getQuestionsForTest).
 */
export function getAnswerableElementsForTest(
  questionsForTest: ILocalizedTestElement[],
  sectionsTime: ITestAnswerTimes,
): ILocalizedTestElement[] {
  const allTestElements = questionsForTest.filter((el) => el.config.type !== QuestionTypes.Text);
  const timedOutElementIds = getTimedOutSectionElementIds(questionsForTest, sectionsTime);
  return allTestElements.filter((el) => !timedOutElementIds.has(el.elementId));
}

/**
 * Builds a map of element IDs that have already been answered, combining
 * persisted answers with in-progress selections that have a `choiceKey`.
 */
export function getAlreadyAnsweredIds(
  persistedAnswers: ITestChoice[],
  currentAnswers: ITestChoice[],
): Map<number, ITestChoice> {
  const answers = new Map<number, ITestChoice>();
  for (const answer of persistedAnswers) {
    answers.set(answer.elementId, answer);
  }
  for (const answer of currentAnswers) {
    if (answer.choiceKey) {
      answers.set(answer.elementId, answer);
    }
  }
  return answers;
}

/**
 * Generates random answers for every unanswered element in `testElements`.
 * Adjacent MostLeast elements are treated as a pair. When one element of a pair is already
 * answered, the partner's existing choice value is excluded from the random selection for
 * the unanswered element.
 */
export function generateRandomAnswersForTest(
  testElements: ILocalizedTestElement[],
  existingAnswers: Map<number, ITestChoice>,
): ITestChoice[] {
  const randomAnswers: ITestChoice[] = [];

  for (let i = 0; i < testElements.length; i++) {
    const element = testElements[i];

    if (element.config.type === QuestionTypes.MostLeast) {
      const next = testElements[i + 1];
      if (next?.config.type !== QuestionTypes.MostLeast) {
        throw new Error(`MostLeast element ${element.elementId} has no adjacent partner`);
      }
      randomAnswers.push(...processMostLeastPair(element, next, existingAnswers));
      i++; // consume the partner
      continue;
    }

    if (!existingAnswers.has(element.elementId)) {
      randomAnswers.push(pickRandomChoice(element));
    }
  }

  return randomAnswers;
}
